// Array-shaped Handlebars helpers for BATCH email templates.
//
// A batch template renders a scope of N transactions:
//   {{#each transactions}} … {{/each}}
// and needs aggregate operations the per-transaction (scalar) helpers in
// ./helpers.ts don't provide — totals, groupings, sort order, date spans.
//
// Every helper here is a PURE function over an array + a dotted path. They are
// registered in THREE places, all of which read `BATCH_HELPERS` so the sets can
// never diverge:
//   1. `ensureHelpers()`      — shared/services/email/EmailService.ts (browser Handlebars)
//   2. helper registration    — supabase/functions/google-api-service/template-pipeline.ts (Deno Handlebars)
//   3. `getHelperScope()`     — ./helpers.ts, the `helpers` argument handed to resolvers
// shared/mirrors/batchEmailHelpers.drift.test.ts asserts 1 and 2 stay in sync.
//
// CALLING CONVENTION: Handlebars always appends its own options-hash object as
// the last argument, so an "optional string" parameter arrives as that object
// when the template omits it. Every optional parameter below is therefore
// guarded with `typeof x === 'string'` — the same shape `productName` uses in
// ./helpers.ts. Resolver code calling these directly through `helpers` passes
// no options object and takes the same path.

import { formatDate, formatPrice, formatWeight } from './helpers.ts';

// ── Path lookup ─────────────────────────────────────────────────────────

/**
 * Deep-lookup handling both dotted and bracket access — same semantics as
 * `getNested` in shared/services/template/validation.ts:
 *   getPath(tx, 'loadPlace.shortName')     → tx.loadPlace.shortName
 *   getPath(tx, 'transports[0].driverName') → tx.transports[0].driverName
 * An empty / missing path returns the item itself, so `sumBy(numbers, '')`
 * works over an array of scalars.
 */
export function getPath(item: unknown, path?: unknown): unknown {
  if (typeof path !== 'string' || path === '') return item;
  const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
  return parts.reduce<unknown>(
    (obj, key) => (obj as Record<string, unknown> | null | undefined)?.[key],
    item,
  );
}

function toArray<T>(arr: unknown): T[] {
  return Array.isArray(arr) ? (arr as T[]) : [];
}

function isEmptyValue(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

/** Numeric coercion that treats '' / null / NaN as "not a number", not as 0. */
function toNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Group key for a value — null/undefined collapse to '' so they group together. */
function groupKey(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) return v.toISOString();
  return JSON.stringify(v);
}

// ── Aggregates ──────────────────────────────────────────────────────────

/** Sum of the numeric values at `path`. Non-numeric and null entries skipped. */
export function sumBy(arr: unknown, path?: unknown): number {
  let total = 0;
  for (const item of toArray(arr)) {
    const n = toNumber(getPath(item, path));
    if (n !== null) total += n;
  }
  return total;
}

/**
 * Count of items whose value at `path` is non-empty. With no `path`, the plain
 * array length. (Grouping lives in `groupBy` — this is the "how many of these
 * actually have a value" counter.)
 */
export function countBy(arr: unknown, path?: unknown): number {
  const items = toArray(arr);
  if (typeof path !== 'string' || path === '') return items.length;
  let count = 0;
  for (const item of items) {
    if (!isEmptyValue(getPath(item, path))) count++;
  }
  return count;
}

/** Mean of the numeric values at `path`. No numeric values → 0. */
export function avgBy(arr: unknown, path?: unknown): number {
  const values: number[] = [];
  for (const item of toArray(arr)) {
    const n = toNumber(getPath(item, path));
    if (n !== null) values.push(n);
  }
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

// ── Shaping ─────────────────────────────────────────────────────────────

export interface GroupedItems<T = unknown> {
  /** The stringified value at `path`; '' for null / undefined. */
  key: string;
  /** The raw (un-stringified) value, for `{{#each}}` bodies that need the object. */
  value: unknown;
  items: T[];
  count: number;
}

/**
 * Group by the value at `path`, preserving first-seen key order.
 * Returned as an ARRAY (not an object) so templates can iterate directly:
 *   {{#each (groupBy transactions "loadPlace.shortName")}}{{key}}: {{count}}{{/each}}
 */
export function groupBy<T = unknown>(arr: unknown, path?: unknown): GroupedItems<T>[] {
  const out: GroupedItems<T>[] = [];
  const index = new Map<string, GroupedItems<T>>();
  for (const item of toArray<T>(arr)) {
    const raw = getPath(item, path);
    const key = groupKey(raw);
    let group = index.get(key);
    if (!group) {
      group = { key, value: raw, items: [], count: 0 };
      index.set(key, group);
      out.push(group);
    }
    group.items.push(item);
    group.count++;
  }
  return out;
}

/** First occurrence per distinct value at `path`, in original order. */
export function uniqueBy<T = unknown>(arr: unknown, path?: unknown): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of toArray<T>(arr)) {
    const key = groupKey(getPath(item, path));
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

/**
 * Stable sort by the value at `path`. Numbers compare numerically, everything
 * else compares as a locale-aware string. Null / undefined always sort LAST
 * regardless of direction — an unset load date shouldn't lead the list.
 */
export function sortBy<T = unknown>(arr: unknown, path?: unknown, direction?: unknown): T[] {
  const desc = typeof direction === 'string' && direction.toLowerCase() === 'desc';
  const decorated = toArray<T>(arr).map((item, i) => ({ item, i, v: getPath(item, path) }));
  decorated.sort((a, b) => {
    const aEmpty = a.v === null || a.v === undefined || a.v === '';
    const bEmpty = b.v === null || b.v === undefined || b.v === '';
    if (aEmpty && bEmpty) return a.i - b.i;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    const an = toNumber(a.v);
    const bn = toNumber(b.v);
    let cmp: number;
    if (an !== null && bn !== null) cmp = an - bn;
    else cmp = String(a.v).localeCompare(String(b.v));
    if (cmp === 0) return a.i - b.i; // stable
    return desc ? -cmp : cmp;
  });
  return decorated.map((d) => d.item);
}

/** Join the non-empty values at `path`. Default separator ', '. */
export function joinBy(arr: unknown, path?: unknown, separator?: unknown): string {
  const sep = typeof separator === 'string' ? separator : ', ';
  const parts: string[] = [];
  for (const item of toArray(arr)) {
    const v = getPath(item, path);
    if (isEmptyValue(v)) continue;
    parts.push(String(v));
  }
  return parts.join(sep);
}

// ── Domain wrappers ─────────────────────────────────────────────────────

export interface DateRangeResult {
  /** Raw ISO-ish minimum value, exactly as it appeared in the data. */
  from: string | null;
  /** Raw ISO-ish maximum value. */
  to: string | null;
  fromFormatted: string;
  toFormatted: string;
  /** true when every non-empty value is the same day (single-day batch). */
  single: boolean;
}

/**
 * Min / max of the date values at `path` across the batch, plus their
 * `formatDate` renderings. Unparseable and empty values are skipped.
 */
export function dateRange(arr: unknown, path?: unknown): DateRangeResult {
  let minTs = Number.POSITIVE_INFINITY;
  let maxTs = Number.NEGATIVE_INFINITY;
  let minRaw: string | null = null;
  let maxRaw: string | null = null;

  for (const item of toArray(arr)) {
    const v = getPath(item, path);
    if (isEmptyValue(v)) continue;
    const raw = v instanceof Date ? v.toISOString() : String(v);
    const ts = new Date(raw).getTime();
    if (Number.isNaN(ts)) continue;
    if (ts < minTs) { minTs = ts; minRaw = raw; }
    if (ts > maxTs) { maxTs = ts; maxRaw = raw; }
  }

  if (minRaw === null) {
    return { from: null, to: null, fromFormatted: '', toFormatted: '', single: false };
  }
  return {
    from: minRaw,
    to: maxRaw,
    fromFormatted: formatDate(minRaw),
    toFormatted: formatDate(maxRaw),
    single: minTs === maxTs,
  };
}

/** Batch total weight, rendered with the same `formatWeight` a single email uses. */
export function totalWeight(arr: unknown, path?: unknown): string {
  return formatWeight(sumBy(arr, path));
}

/**
 * Batch total price. Sums per currency (a batch can legitimately mix PLN and
 * EUR loads) and renders each bucket with `formatPrice`, joined by ' + '.
 * `currencyPath` may be omitted — then everything sums into one unlabelled bucket.
 */
export function totalPrice(arr: unknown, pricePath?: unknown, currencyPath?: unknown): string {
  const buckets = new Map<string, number>();
  const order: string[] = [];
  for (const item of toArray(arr)) {
    const n = toNumber(getPath(item, pricePath));
    if (n === null) continue;
    const rawCurrency = typeof currencyPath === 'string' ? getPath(item, currencyPath) : null;
    const currency = isEmptyValue(rawCurrency) ? '' : String(rawCurrency);
    if (!buckets.has(currency)) order.push(currency);
    buckets.set(currency, (buckets.get(currency) ?? 0) + n);
  }
  if (order.length === 0) return '';
  return order
    .map((currency) => formatPrice({ price: buckets.get(currency), priceCurrency: currency }))
    .map((s) => s.trim())
    .join(' + ');
}

// ── Registration source of truth ────────────────────────────────────────

/**
 * THE list both Handlebars instances and `getHelperScope()` register from.
 * Adding a helper here makes it available in templates (FE + edge) and in
 * resolvers at once; the drift test fails if either registration site forgets it.
 */
export const BATCH_HELPERS = Object.freeze({
  sumBy,
  countBy,
  avgBy,
  groupBy,
  uniqueBy,
  sortBy,
  joinBy,
  dateRange,
  totalWeight,
  totalPrice,
});

export type BatchHelperName = keyof typeof BATCH_HELPERS;
